/**
 * Types for Jira issue → Pull Request hydration.
 *
 * Used by the frontend hydration path to enrich Jira search results
 * with linked PR data from devOps.ariGraph.issueAssociatedPr (UGS via AGG).
 * Supports PRs from all providers (GitHub, GitLab, Bitbucket, etc.).
 */

export const DevOpsPullRequestStatus = {
	Open: 'OPEN',
	Merged: 'MERGED',
	Declined: 'DECLINED',
	Draft: 'DRAFT',
	Unknown: 'UNKNOWN',
} as const;

export type DevOpsPullRequestStatusType =
	(typeof DevOpsPullRequestStatus)[keyof typeof DevOpsPullRequestStatus];

export type DevOpsPullRequestApprovalStatus = 'APPROVED' | 'NEEDSWORK' | 'UNAPPROVED';

export type DevOpsReviewer = {
	approvalStatus: DevOpsPullRequestApprovalStatus;
};

export type HydratedPullRequest = {
	id: string;
	title: string | null;
	url: string | null;
	pullRequestInternalId: string | null;
	status: DevOpsPullRequestStatusType | null;
	lastUpdated: string | null;
	reviewers: DevOpsReviewer[];
};

/**
 * Response shape for a single aliased issueAssociatedPr query.
 * Each alias returns a connection of PR edges.
 */
export type IssueAssociatedPrConnection = {
	edges:
		| {
				id: string;
				node: {
					__typename?: string;
					id: string;
					title: string | null;
					url: string | null;
					pullRequestInternalId: string | null;
					status: DevOpsPullRequestStatusType | null;
					lastUpdated: string | null;
					reviewers: DevOpsReviewer[] | null;
				} | null;
		  }[]
		| null;
} | null;

/**
 * Top-level response shape from the PR hydration AGG query.
 * Keys under graphStore are dynamic aliases like "issue0", "issue1", etc.
 */
export type JiraPRHydrationData = {
	graphStore: Record<string, IssueAssociatedPrConnection>;
};

export type IssueLinkedPRsLoaded = {
	readonly status: 'loaded';
	readonly prs: HydratedPullRequest[];
	/** True when the API returned its maximum, meaning there could be more PRs beyond what was fetched */
	readonly hasMore: boolean;
};

export type IssueLinkedPRsError = {
	readonly status: 'error';
};

export type IssueLinkedPRsEntry = IssueLinkedPRsLoaded | IssueLinkedPRsError;

/**
 * Mapping from Jira issue ARI → hydrated PRs with overflow indicator.
 * An absent key means the issue hasn't been fetched yet.
 */
export type IssueLinkedPRs = Record<string, IssueLinkedPRsEntry>;

/**
 * Result of parseJiraPRResponse — always contains loaded entries.
 */
export type ParsedIssueLinkedPRs = Record<string, IssueLinkedPRsLoaded>;
